// MP135 G2 — Sentry wrapper for the Web app. Mirrors the API shape
// (apps/api/src/lib/sentry.ts) so call sites look the same on both
// sides. Init runs once from index.tsx before <App /> mounts;
// captureException is a silent no-op when VITE_SENTRY_DSN is unset
// so local dev / preview builds don't ship to a real Sentry project.

import * as Sentry from '@sentry/react';
import { scrubBreadcrumb, scrubEvent } from './sentryScrub';

let initialized = false;

interface ViteEnv {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  PROD?: boolean;
}

function readEnv(): ViteEnv {
  return (import.meta as unknown as { env?: ViteEnv }).env ?? {};
}

export function initWebSentry(): void {
  if (initialized) return;
  const env = readEnv();
  if (!env.VITE_SENTRY_DSN) {
    // MP402 — one line, once, so "is error reporting on?" is answered
    // by opening the console rather than reading the build config.
    // eslint-disable-next-line no-console
    console.info('[sentry] VITE_SENTRY_DSN not set — error reporting disabled (no-op)');
    return;
  }

  const sampleRate = env.VITE_SENTRY_TRACES_SAMPLE_RATE
    ? Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE)
    : env.PROD
      ? 0.1
      : 0;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT,
    release: env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number.isFinite(sampleRate) ? sampleRate : 0,
    // PII is opt-in. The auth context attaches the active user/org
    // explicitly via setSentryUser() below once the session loads,
    // so we get user-keyed errors without leaking emails into the
    // default request body / IP.
    sendDefaultPii: false,
    // sendDefaultPii does not cover URLs. Strip query strings and
    // fragments (OAuth codes/state, invite tokens, ?next=…) from every
    // URL in breadcrumbs, errors and transactions; keep the pathname.
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    beforeSend: (event) => scrubEvent(event),
    beforeSendTransaction: (event) => scrubEvent(event),
  });
  initialized = true;
}

/** Attach the active user + org to every subsequent Sentry event.
 *  Called from the auth context once a session is loaded; cleared
 *  on sign-out via clearSentryUser(). */
export function setSentryUser(user: { uid: string; orgId?: string | null }): void {
  if (!initialized) return;
  Sentry.setUser({ id: user.uid });
  if (user.orgId) Sentry.setTag('orgId', user.orgId);
}

export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}

export function captureException(
  err: unknown,
  context?: { route?: string; tags?: Record<string, string> },
): void {
  if (!initialized) {
    // eslint-disable-next-line no-console
    console.error('[sentry-noop]', err, context);
    return;
  }
  Sentry.withScope((scope) => {
    if (context?.route) scope.setTag('route', context.route);
    if (context?.tags) {
      for (const [k, v] of Object.entries(context.tags)) scope.setTag(k, v);
    }
    Sentry.captureException(err);
  });
}

/** Re-export of Sentry's `<ErrorBoundary>` so call sites import from
 *  this module instead of `@sentry/react` directly. Keeps the
 *  provider swap surface small. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/** MP142 — Friendly-error helper.
 *
 *  Catches at UI boundaries — surfaces a short user-facing message
 *  while sending the raw error + route metadata to Sentry for the
 *  engineering team. Use everywhere a user clicks a button and
 *  things might break: API failures, PDF generation, file download
 *  errors, etc.
 *
 *  The returned `message` is what to render to the user.
 *  Defaults to a polite generic — pass an explicit `userMessage` if
 *  you want surface-specific copy ("Couldn't generate your report",
 *  "Couldn't load that page").
 *
 *  `eventId` is the Sentry event id (if Sentry initialised). Some
 *  surfaces include it as "Reference: <id>" so support can correlate.
 */
export function reportError(
  err: unknown,
  opts: {
    route?: string;
    userMessage?: string;
    tags?: Record<string, string>;
  } = {},
): { message: string; eventId: string | null } {
  const userMessage =
    opts.userMessage ?? 'Something went wrong. Try again, or contact support if this keeps happening.';

  if (!initialized) {
    // eslint-disable-next-line no-console
    console.error('[sentry-noop]', err, opts);
    return { message: userMessage, eventId: null };
  }

  let eventId: string | null = null;
  Sentry.withScope((scope) => {
    if (opts.route) scope.setTag('route', opts.route);
    if (opts.tags) {
      for (const [k, v] of Object.entries(opts.tags)) scope.setTag(k, v);
    }
    eventId = Sentry.captureException(err) ?? null;
  });
  return { message: userMessage, eventId };
}
